import type {
  ArtifactVersionMetricChartRow,
  ArtifactVersionMetricPanelConfig,
} from './types';

export function buildVersionLabelByXValue(
  rows: ArtifactVersionMetricChartRow[]
): Map<number, string> {
  const labelByIndex = new Map<number, string>();
  [...rows]
    .sort(compareArtifactVersionMetricRowsByVersion)
    .forEach((row, index) => {
      labelByIndex.set(index, row.label);
    });
  return labelByIndex;
}

export function formatArtifactVersionMetricVersionTick(
  value: number,
  labelByXValue: Map<number, string>
): string {
  return labelByXValue.get(Math.round(value)) ?? String(Math.round(value));
}

export function getArtifactVersionMetricChartTitle(
  config: ArtifactVersionMetricPanelConfig
): string | undefined {
  if (config.chartTitle === null) {
    return undefined;
  }
  if (config.chartTitle != null && config.chartTitle !== '') {
    return config.chartTitle;
  }

  return getArtifactVersionMetricDefaultChartTitle(config);
}

export function getArtifactVersionMetricDefaultChartTitle(
  config: Pick<
    ArtifactVersionMetricPanelConfig,
    'chartType' | 'metricName' | 'xMetricName'
  >
): string | undefined {
  if (config.metricName == null || config.metricName === '') {
    return undefined;
  }

  if (
    config.chartType === 'scatter' &&
    config.xMetricName != null &&
    config.xMetricName !== ''
  ) {
    return `${config.metricName} vs ${config.xMetricName}`;
  }

  return config.metricName;
}

export function compareArtifactVersionMetricRowsByVersion(
  a: ArtifactVersionMetricChartRow,
  b: ArtifactVersionMetricChartRow
): number {
  if (
    a.versionIndex != null &&
    Number.isFinite(a.versionIndex) &&
    b.versionIndex != null &&
    Number.isFinite(b.versionIndex) &&
    a.versionIndex !== b.versionIndex
  ) {
    return a.versionIndex - b.versionIndex;
  }

  const aLabelIndex = parseArtifactVersionMetricLabelIndex(a.label);
  const bLabelIndex = parseArtifactVersionMetricLabelIndex(b.label);
  if (
    aLabelIndex != null &&
    bLabelIndex != null &&
    aLabelIndex !== bLabelIndex
  ) {
    return aLabelIndex - bLabelIndex;
  }

  return a.selectionIndex - b.selectionIndex;
}

function parseArtifactVersionMetricLabelIndex(label: string): number | null {
  const match = /^v(\d+)$/.exec(label);
  if (match == null) {
    return null;
  }

  const parsedIndex = Number(match[1]);
  return Number.isFinite(parsedIndex) ? parsedIndex : null;
}

export function getArtifactVersionMetricYDomainFromValues(
  values: number[],
  config: Pick<
    ArtifactVersionMetricPanelConfig,
    'yAxisMin' | 'yAxisMax' | 'yLogScale'
  >
): [number, number] {
  return getNumericAxisDomain(
    values,
    config.yAxisMin,
    config.yAxisMax,
    config.yLogScale
  );
}

export function getArtifactVersionMetricScatterXDomain(
  xValues: number[],
  config: Pick<
    ArtifactVersionMetricPanelConfig,
    'xAxisMin' | 'xAxisMax' | 'xLogScale'
  >
): [number, number] | null {
  if (xValues.length === 0) {
    return null;
  }
  return getNumericAxisDomain(
    xValues,
    config.xAxisMin,
    config.xAxisMax,
    config.xLogScale
  );
}

function getNumericAxisDomain(
  values: number[],
  configuredMin?: number | null,
  configuredMax?: number | null,
  logScale?: boolean
): [number, number] {
  const expandDomainValue = (value: number) => Math.abs(value) * 0.1 || 1;
  const dataMin = values.length > 0 ? Math.min(...values) : 0;
  const dataMax = values.length > 0 ? Math.max(...values) : 1;
  const hasConfiguredMin =
    configuredMin != null && Number.isFinite(configuredMin);
  const hasConfiguredMax =
    configuredMax != null && Number.isFinite(configuredMax);

  let min = hasConfiguredMin ? configuredMin : Math.min(dataMin, 0);
  let max = hasConfiguredMax ? configuredMax : dataMax;

  if (min > max || (min === max && hasConfiguredMin && hasConfiguredMax)) {
    if (hasConfiguredMin && hasConfiguredMax) {
      min = Math.min(dataMin, 0);
      max = dataMax;
    } else if (hasConfiguredMin) {
      max = min + expandDomainValue(min);
    } else {
      min = max - expandDomainValue(max);
    }
  }

  if (min === max) {
    if (min === 0) {
      max = 1;
    } else {
      const padding = expandDomainValue(min);
      min = min - padding;
      max = max + padding;
    }
  }

  if (logScale === true && min <= 0) {
    const positiveValues = values.filter(value => value > 0);
    min = positiveValues.length > 0 ? Math.min(...positiveValues) : 1e-6;
  }

  return [min, max];
}

export function getArtifactVersionMetricXDomain(
  rows: ArtifactVersionMetricChartRow[]
): [number, number] | null {
  if (rows.length === 0) {
    return null;
  }

  return [-0.5, rows.length - 0.5];
}

export function isArtifactVersionMetricDualMetricScatter(
  config: ArtifactVersionMetricPanelConfig
): boolean {
  return (
    config.chartType === 'scatter' &&
    config.xMetricName != null &&
    config.xMetricName !== ''
  );
}
